import { lazy, Suspense, useState } from "react";
import { FACE_BY_ID } from "~/lib/faces";
import { useMarkFaceVisited } from "~/lib/hooks/useMarkFaceVisited";
import { useFaceSeo } from "~/lib/seo/useSeo";

const EngineStage = lazy(() =>
  import("~/three/EngineStage").then((m) => ({ default: m.EngineStage })),
);

interface ShippedTitle {
  id: string;
  title: string;
  engine: "Unity" | "Unreal";
  blurb: string;
  link?: string;
}

const TITLES: readonly ShippedTitle[] = [
  {
    id: "cubek",
    title: "Cubek",
    engine: "Unity",
    blurb:
      "Rubik's-cube simulator on Google Play. AI Solver, AR Mode, Rhythm Hints. Same engine — fully native Android build pipeline.",
    link: "https://play.google.com/store/apps/details?id=com.JGSON.Cubek",
  },
  {
    id: "doodle-jump-adventure",
    title: "Doodle Jump Adventure",
    engine: "Unity",
    blurb:
      "Jump-action game in the Doodle Jump franchise (Lima Sky). Unity developer at Oktagon Games, 2019. Retired from the official stores; still listed on QooApp.",
    link: "https://m-apps.qoo-app.com/en-US/app/10205",
  },
  {
    id: "magic-puzzle-quest",
    title: "Magic: Puzzle Quest",
    engine: "Unity",
    blurb:
      "Match-3 + RPG on the Magic: The Gathering IP. Unity developer at Oktagon Games (2019); published by D3 Publisher. iOS + Android.",
    link: "https://play.google.com/store/apps/details?id=com.d3p.olympic",
  },
  {
    id: "ra-brasil",
    title: "Realidade Aumentada Brasil",
    engine: "Unity",
    // TODO João: context, year, scope.
    blurb: "AR initiative for the Brazilian market.",
  },
];

export function Engine() {
  const face = FACE_BY_ID.engine;
  useMarkFaceVisited(face.id);
  useFaceSeo(face);
  const [unityLoaded, setUnityLoaded] = useState(false);

  return (
    <section className="flex w-full flex-col gap-10 pb-16">
      <div className="relative h-60 w-full overflow-hidden border-b border-jg-border/40 sm:h-72">
        <Suspense
          fallback={
            <div
              aria-hidden="true"
              className="absolute inset-0 bg-gradient-to-b from-jg-engine/15 to-transparent"
            />
          }
        >
          <EngineStage />
        </Suspense>

        <div className="pointer-events-none absolute right-4 top-4 sm:right-6">
          <span
            className="font-mono text-xs uppercase tracking-[0.25em]"
            style={{ color: face.themeColor }}
          >
            Cubie · {face.label}
          </span>
        </div>
      </div>

      <header className="mx-auto w-full max-w-4xl px-4">
        <h1 className="font-display text-3xl font-semibold leading-tight tracking-tight text-jg-fg sm:text-4xl">
          <span style={{ color: face.themeColor }}>Unity</span> &amp;{" "}
          <span style={{ color: face.themeColor }}>Unreal</span>, end to end
        </h1>
        <p className="mt-2 text-sm leading-relaxed text-jg-fg-muted sm:text-base">
          From gameplay programming and custom rendering to multiplayer
          netcode and editor tooling. Ten years across mobile, PC, and XR.
        </p>
      </header>

      <div className="mx-auto flex w-full max-w-4xl flex-col gap-8 px-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="rounded-2xl border border-jg-border bg-jg-bg-soft/40 p-5">
            <p
              className="font-mono text-[10px] uppercase tracking-[0.25em]"
              style={{ color: face.themeColor }}
            >
              Unity (C#)
            </p>
            <ul className="mt-3 space-y-2 text-sm text-jg-fg-muted">
              <li>
                <span className="text-jg-fg">Gameplay programming:</span>{" "}
                state machines, ECS / DOTS where it earns its keep,
                netcode-aware patterns.
              </li>
              <li>
                <span className="text-jg-fg">URP / Shader Graph:</span>{" "}
                custom stylized rendering, mobile-first lighting budgets.
              </li>
              <li>
                <span className="text-jg-fg">XR + AR Foundation:</span>{" "}
                shared codepath for ARCore + ARKit + Quest, ScriptableObject
                content authoring.
              </li>
              <li>
                <span className="text-jg-fg">Editor tooling:</span> custom
                inspectors, asset-postprocessing pipelines, build automation
                via Cloud Build / GitHub Actions.
              </li>
            </ul>
          </div>

          <div className="rounded-2xl border border-jg-border bg-jg-bg-soft/40 p-5">
            <p
              className="font-mono text-[10px] uppercase tracking-[0.25em]"
              style={{ color: face.themeColor }}
            >
              Unreal (C++ &amp; Blueprint)
            </p>
            <ul className="mt-3 space-y-2 text-sm text-jg-fg-muted">
              <li>
                <span className="text-jg-fg">Gameplay framework:</span>{" "}
                Actors, Components, GAS (Gameplay Ability System), Enhanced
                Input.
              </li>
              <li>
                <span className="text-jg-fg">Rendering pipeline:</span>{" "}
                Niagara VFX, custom materials (HLSL + node graphs), Lumen
                + Nanite tuning for stylized + photoreal looks.
              </li>
              <li>
                <span className="text-jg-fg">Multiplayer:</span> replication,
                RPCs, dedicated-server builds, deterministic netcode for
                competitive titles.
              </li>
              <li>
                <span className="text-jg-fg">Editor extensions:</span>{" "}
                Slate UI, custom asset types, automation tooling so
                non-engineers can ship content.
              </li>
            </ul>
          </div>
        </div>

        <div className="rounded-2xl border border-jg-border bg-jg-bg-soft/40 p-5">
          <p
            className="font-mono text-[10px] uppercase tracking-[0.25em]"
            style={{ color: face.themeColor }}
          >
            Shared stack
          </p>
          <ul className="mt-3 flex flex-wrap gap-1.5">
            {[
              "C#",
              "C++",
              "Blueprint",
              "Shader Graph",
              "HLSL",
              "URP",
              "Lumen",
              "Nanite",
              "Niagara",
              "AR Foundation",
              "Vuforia",
              "Quest XR",
              "Steam SDK",
              "Play Console",
            ].map((t) => (
              <li
                key={t}
                className="rounded-full border border-jg-border bg-jg-bg/40 px-2.5 py-0.5 text-xs text-jg-fg-muted"
              >
                {t}
              </li>
            ))}
          </ul>
        </div>

        <div className="flex flex-col gap-3">
          <h3
            className="font-mono text-xs uppercase tracking-[0.25em]"
            style={{ color: face.themeColor }}
          >
            Shipped titles
          </h3>
          <ul className="grid gap-3 sm:grid-cols-2">
            {TITLES.map((t) => {
              const card = (
                <div className="flex h-full flex-col gap-2 rounded-2xl border border-jg-border bg-jg-bg-soft/40 p-4 transition-colors group-hover:border-jg-engine/40">
                  <div className="flex items-baseline justify-between gap-3">
                    <h4 className="font-display text-base font-semibold leading-tight">
                      {t.title}
                    </h4>
                    <span
                      className="shrink-0 font-mono text-[10px] uppercase tracking-[0.18em]"
                      style={{ color: face.themeColor }}
                    >
                      {t.engine}
                    </span>
                  </div>
                  <p className="text-sm leading-relaxed text-jg-fg-muted">
                    {t.blurb}
                  </p>
                </div>
              );
              return (
                <li key={t.id}>
                  {t.link ? (
                    <a
                      href={t.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group block h-full"
                    >
                      {card}
                    </a>
                  ) : (
                    <div className="group block h-full">{card}</div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>

        <div className="flex flex-col gap-3">
          <h3
            className="font-mono text-xs uppercase tracking-[0.25em]"
            style={{ color: face.themeColor }}
          >
            Playable Unity demo
          </h3>

          {unityLoaded ? (
            <div className="relative aspect-video w-full overflow-hidden rounded-2xl border border-jg-border bg-jg-bg-soft/40">
              <iframe
                src="/unity/asteroids/index.html"
                title="Asteroids — Unity WebGL build by João Vidotto"
                className="absolute inset-0 h-full w-full"
                allow="fullscreen; gamepad; autoplay; cross-origin-isolated"
                loading="lazy"
              />
            </div>
          ) : (
            <div className="flex flex-col gap-4 rounded-2xl border border-jg-border bg-jg-bg-soft/40 p-5">
              <div>
                <h4 className="font-display text-lg font-semibold leading-tight">
                  Asteroids — Unity WebGL
                </h4>
                <p className="mt-2 text-sm leading-relaxed text-jg-fg-muted">
                  The vector animation at the top of this cubie is the
                  homage; this is the real thing. A playable Unity build
                  of Asteroids, compiled to WebGL, gated behind a
                  click-to-load button so it doesn't block first paint.
                  Same engine + pipeline pitched by the cards above.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setUnityLoaded(true)}
                className="self-start rounded-full px-4 py-2 text-sm font-semibold transition-opacity hover:opacity-90"
                style={{
                  backgroundColor: face.themeColor,
                  color: "#0a0a0f",
                }}
              >
                ▶ Load Asteroids (Unity build, ~16 MB)
              </button>
              <p className="text-xs text-jg-fg-muted">
                Brotli-compressed WebGL bundle. Loads only after you
                tap — keeps the initial page fast.
              </p>
            </div>
          )}
        </div>

        <div className="rounded-2xl border border-dashed border-jg-border bg-jg-bg-soft/30 p-5 text-sm text-jg-fg-muted">
          <strong className="block font-display text-jg-fg">
            About the animation above
          </strong>
          <p className="mt-2">
            Vector wireframe homage to Atari Asteroids (1979) — wrap-around
            play area, sin-jittered drift, bloom-driven phosphor glow.
            Renders with native Three.js{" "}
            <code className="rounded bg-jg-bg-soft/60 px-1 py-0.5 text-xs">
              lineLoop
            </code>{" "}
            primitives, no library overhead. The real Unity build of the
            same game is the Load button above.
          </p>
        </div>
      </div>
    </section>
  );
}
